import { HttpClient } from '@angular/common/http';
import { AccountTypeList, BadgeList, UserStatus } from '@app/shared/const';
import { User, UserModel } from '@app/shared/models/user';
import { UserManagementReport } from '@app/shared/models/users-management';
import { environment } from '@env/environment';
import * as moment from 'moment';
import { Observable } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { ApiClientConfig } from '../api-client.config';
import {
  AddModeratorResponse,
  GetAffiliateDetailParams,
  GetAffiliateDetailResponse,
  Moderator,
  ModeratorRequest,
  ModeratorResponse,
  ModeratorRole,
  ModeratorRoleResponse,
  UserInfoResponse,
  UserManagementResponse
} from './api.user.model';

export class UserApi {
  private apiUrl = environment.API_URL + '/user';

  constructor(
    public readonly http: HttpClient,
    public config: ApiClientConfig
  ) {}

  getUserById(id: string): Observable<UserInfoResponse> {
    return this.http.get<any>(this.apiUrl + '/users/' + id).pipe(
      timeout(this.config.responseTimeout),
      map((res: any) => {
        return res;
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  getTotalsUser(roles: any, statuses: any, keyword: string) {
    return this.http.get<UserModel[]>(this.apiUrl + '/users').pipe(
      timeout(this.config.responseTimeout),
      map((res: any) => {
        const users = res.data;
        res.data = users.filter((item: any) => {
          if (keyword.trim() !== '') {
            return (
              (item.last_name?.toLowerCase().includes(keyword.toLowerCase()) ||
                item.first_name?.toLowerCase().includes(keyword.toLowerCase()) ||
                item.email?.toLowerCase().includes(keyword.toLowerCase()) ||
                item.register_date?.toLowerCase().includes(keyword.toLowerCase())) &&
              (roles && roles.length > 0
                ? roles.some((element: any) => item.roles.includes(element))
                : true) &&
              (statuses && statuses.length > 0 ? statuses.includes(item.status) : true)
            );
          } else {
            return (
              (roles && roles.length > 0
                ? roles.some((element: any) => item.roles.includes(element))
                : true) && (statuses && statuses.length > 0 ? statuses.includes(item.status) : true)
            );
          }
        });
        return res.data;
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  updateUser(userId: string, userDetail: Partial<any>): Observable<any> {
    return this.http.put<any>(this.apiUrl + '/users/' + userId, userDetail).pipe(
      timeout(this.config.responseTimeout),
      map((res: any) => {
        return res.data;
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  delete(id: string) {
    return this.http.delete<User>(this.apiUrl + '/users/' + id).pipe(
      timeout(this.config.responseTimeout),
      catchError(error => {
        throw error.error;
      })
    );
  }

  getUserManagementReport(): Observable<UserManagementReport> {
    return this.http.get<any>(this.apiUrl + '/admin/user-management-report').pipe(
      timeout(this.config.responseTimeout),
      map((res: any) => {
        if (res.success) {
          return res.data;
        }
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  getUserManagement(
    pageNum: number,
    pageSize: number,
    textSearch: string,
    filterData?: any
  ): Observable<UserManagementResponse> {
    let url = `/admin/search?pageNum=${pageNum}&pageSize=${pageSize}`;
    if (textSearch && textSearch !== '') url = url + `&textSearch=${textSearch}`;
    if (filterData && filterData.selectedRoles) url = url + `&roles=${filterData.selectedRoles}`;
    if (filterData && filterData.selectedStatuses && filterData.selectedStatuses.length) {
      url = url + `&status=${filterData.selectedStatuses}`;
    } else {
      const statusIds = Object.values(UserStatus).map(status => status.id);
      const statusQueryString = statusIds.join(',');
      url = url + `&status=${statusQueryString}`;
    }
    if (filterData && filterData.verifiedAccount) {
      url = url + `&verifiedAccount=${filterData.verifiedAccount}`;
    }
    if (
      filterData &&
      filterData.userBadge &&
      filterData.userBadge.length &&
      filterData.userBadge.length < BadgeList.length
    ) {
      url = url + `&userBadge=${filterData.userBadge}`;
    }

    if (filterData && filterData.selectedDateRange && filterData.selectedDateRange.length) {
      const startTime = moment(filterData.selectedDateRange[0]).format('MM/DD/YYYY');

      const endTime =
        filterData.selectedDateRange[1] === null
          ? moment(filterData.selectedDateRange[0]).format('MM/DD/YYYY')
          : moment(filterData.selectedDateRange[1]).format('MM/DD/YYYY');

      url += `&startTime=${startTime}&endTime=${endTime}`;
    }

    if (filterData && filterData.selectedAccountTypes && filterData.selectedAccountTypes.length) {
      const accountTypeIds = filterData.selectedAccountTypes.map(
        (account: { id: any }) => account.id
      );
      const accountTypeQueryString = accountTypeIds.join(',');
      url += `&accountType=${accountTypeQueryString}`;
    } else {
      const accountTypeIds = Object.values(AccountTypeList).map(account => account.id);
      const accountTypeQueryString = accountTypeIds.join(',');
      url += `&accountType=${accountTypeQueryString}`;
    }

    return this.http.get<UserManagementResponse>(this.apiUrl + url).pipe(
      timeout(this.config.responseTimeout),
      map((res: UserManagementResponse) => {
        return res;
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  getCountries(): Observable<any> {
    return this.http.get<any>(`${environment.API_URL}/booking-generic/country/list`).pipe(
      timeout(this.config.responseTimeout),
      map((res: any) => {
        return res.data;
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  getModerateRole(body: ModeratorRequest): Observable<string> {
    return this.http.post<any>(`${this.apiUrl}/admin/role`, body).pipe(
      timeout(this.config.responseTimeout),
      map((res: any) => {
        return res.data.role;
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  addModerator(body: ModeratorRequest): Observable<Moderator[]> {
    return this.http.post<AddModeratorResponse>(`${this.apiUrl}/admin/moderator`, body).pipe(
      timeout(this.config.responseTimeout),
      map(res => {
        return res.data;
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  getModerators(
    pageNum: number,
    pageSize: number,
    nodeId: string,
    param: ModeratorRequest
  ): Observable<ModeratorResponse> {
    const url = `${this.apiUrl}/admin/moderators?pageNum=${pageNum}&pageSize=${pageSize}&nodeId=${nodeId}&countryCode=${param.country_code}&zipCode=${param.zip_code}&type=${param.type}&typeModerator=${param.type_moderator}`;
    return this.http.get<ModeratorResponse>(url).pipe(
      timeout(this.config.responseTimeout),
      map(res => {
        return res;
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  getModeratorRole(): Observable<ModeratorRole[]> {
    return this.http.get<ModeratorRoleResponse>(`${this.apiUrl}/admin/roles/module`).pipe(
      timeout(this.config.responseTimeout),
      map(res => {
        return res.data;
      }),
      catchError(error => {
        window.location.href = `${environment.ADMIN_HUB_URL}`;
        throw error.error;
      })
    );
  }

  updateUserBadge(userId: string, badge: string, isUpdate: boolean, body: any): Observable<any> {
    return this.http
      .put<any>(
        `${this.apiUrl}/admin/user/${userId}/brands?brandsOnSoctrip=${badge}&isUpdate=${isUpdate}`,
        body
      )
      .pipe(
        timeout(this.config.responseTimeout),
        map(res => {
          return res;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  deleteUserBadge(userId: string, badge: string, body: any): Observable<any> {
    return this.http
      .delete<any>(`${this.apiUrl}/admin/user/${userId}/brands?brandsOnSoctrip=${badge}`, { body })
      .pipe(
        timeout(this.config.responseTimeout),
        map(res => {
          return res;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  getAffiliateMarketerGroups(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/affiliate-marketer/groups`).pipe(
      timeout(this.config.responseTimeout),
      map(res => {
        return res;
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  getAffiliateMarketerUsers(
    groupId: string,
    isAddUser: boolean,
    pageNum: number,
    pageSize: number,
    textSearch: string
  ): Observable<any> {
    const url = `${this.apiUrl}/affiliate-marketer/users?group_id=${groupId}&isAddUser=${isAddUser}&pageNum=${pageNum}&pageSize=${pageSize}&textSearch=${textSearch}`;
    return this.http.get<any>(url).pipe(
      timeout(this.config.responseTimeout),
      map(res => {
        return res;
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  getAffiliateMarketerUserDetail(
    groupId: string,
    userId: string,
    pageNum: number,
    pageSize: number,
    textSearch: string,
    startTime: string,
    endTime: string
  ): Observable<any> {
    const url = `${this.apiUrl}/affiliate-marketer/users-details?group_id=${groupId}&user_id=${userId}&pageNum=${pageNum}&pageSize=${pageSize}&startTime=${startTime}&endTime=${endTime}&textSearch=${textSearch}`;
    return this.http.get<any>(url).pipe(
      timeout(this.config.responseTimeout),
      map(res => {
        return res;
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  addAffiliateMarketer(groupId: string, userIdList: string[]): Observable<any> {
    const body = {
      user_ids: userIdList,
      group_id: groupId
    };
    return this.http.post<any>(`${this.apiUrl}/affiliate-marketer/user`, body).pipe(
      timeout(this.config.responseTimeout),
      map(res => {
        return res;
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  deleteAffiliateMarketer(groupId: string, userId: string): Observable<any> {
    const body = {
      user_ids: [userId],
      group_id: groupId
    };
    return this.http.delete<any>(`${this.apiUrl}/affiliate-marketer/user`, { body }).pipe(
      timeout(this.config.responseTimeout),
      map(res => {
        return res;
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  getUserGrowth(
    countryCode: string,
    viewBy: string,
    startTime: string,
    endTime: string
  ): Observable<any> {
    return this.http
      .get<any>(
        `${this.apiUrl}/dashboard/user-growth?country_code=${countryCode}&enumViewBy=${viewBy}&startTime=${startTime}&endTime=${endTime}`
      )
      .pipe(
        timeout(this.config.responseTimeout),
        map(res => {
          return res;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  getUserPostGrowth(
    countryCode: string,
    viewBy: string,
    startTime: string,
    endTime: string
  ): Observable<any> {
    return this.http
      .get<any>(
        `${this.apiUrl}/dashboard/post-growth?country_code=${countryCode}&enumViewBy=${viewBy}&startTime=${startTime}&endTime=${endTime}`
      )
      .pipe(
        timeout(this.config.responseTimeout),
        map(res => {
          return res;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  getUserEngagement(
    countryCode: string,
    viewBy: string,
    startTime: string,
    endTime: string
  ): Observable<any> {
    return this.http
      .get<any>(
        `${this.apiUrl}/dashboard/engagement?country_code=${countryCode}&enumViewBy=${viewBy}&startTime=${startTime}&endTime=${endTime}`
      )
      .pipe(
        timeout(this.config.responseTimeout),
        map(res => {
          return res;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  getVerifiedUserOverview(
    countryCode: string,
    viewBy: string,
    startTime: string,
    endTime: string
  ): Observable<any> {
    return this.http
      .get<any>(
        `${this.apiUrl}/dashboard/verified-user-overview?country_code=${countryCode}&enumViewBy=${viewBy}&startTime=${startTime}&endTime=${endTime}`
      )
      .pipe(
        timeout(this.config.responseTimeout),
        map(res => {
          return res;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  getUserByAge(
    countryCode: string,
    startDateEpochMillis: number,
    endDateEpochMillis: number
  ): Observable<any> {
    return this.http
      .get<any>(
        `${this.apiUrl}/dashboard/age-count?country_code=${countryCode}&startDateEpochMillis=${startDateEpochMillis}&endDateEpochMillis=${endDateEpochMillis}`
      )
      .pipe(
        timeout(this.config.responseTimeout),
        map(res => {
          return res;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  getUserByGender(countryCode: string, startTime: string, endTime: string): Observable<any> {
    return this.http
      .get<any>(
        `${this.apiUrl}/dashboard/user-by-gender?country_code=${countryCode}&startTime=${startTime}&endTime=${endTime}`
      )
      .pipe(
        timeout(this.config.responseTimeout),
        map(res => {
          return res;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  getUserTopXu(countryCode: string, startTime: string, endTime: string): Observable<any> {
    return this.http
      .get<any>(
        `${this.apiUrl}/dashboard/user-top-xu?country_code=${countryCode}&startTime=${startTime}&endTime=${endTime}`
      )
      .pipe(
        timeout(this.config.responseTimeout),
        map(res => {
          return res;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  getUserTopFollower(countryCode: string, startTime: string, endTime: string): Observable<any> {
    return this.http
      .get<any>(
        `${this.apiUrl}/dashboard/user-top-follower?country_code=${countryCode}&startTime=${startTime}&endTime=${endTime}`
      )
      .pipe(
        timeout(this.config.responseTimeout),
        map(res => {
          return res;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  getUserSummary(countryCode: string, startTime: string, endTime: string): Observable<any> {
    return this.http
      .get<any>(
        `${this.apiUrl}/dashboard/management-user?country_code=${countryCode}&startTime=${startTime}&endTime=${endTime}`
      )
      .pipe(
        timeout(this.config.responseTimeout),
        map(res => {
          return res;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  getAffiliateDetails(params: GetAffiliateDetailParams): Observable<GetAffiliateDetailResponse> {
    return this.http
      .get<GetAffiliateDetailResponse>(`${this.apiUrl}/affiliate-marketer/users-details`, {
        params: { ...params }
      })
      .pipe(
        timeout(this.config.responseTimeout),
        map(res => {
          return res;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  getPermissionCodeList() {
    return this.http
      .get<any>(`${this.apiUrl}/admin-permissions/hub/SOCIAL_MODULE_ADMIN/permission`)
      .pipe(
        timeout(this.config.responseTimeout),
        map((res: any) => {
          return res;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }
}
